import React, { FC, useEffect, useMemo, useState, memo } from "react";
import { useSelector } from "react-redux";
import { ResourceTypeLink } from "@evergis/api";
import { Dialog, DialogContent, IButtonItemProps } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { Title } from "./Title";
import { Content } from "../styled";
import { ExportDialogContent } from "./Content";
import { Actions } from "./Actions";

import { EXPORT_DIALOG_STYLE, EXPORT_FILE_TYPES } from "../constants";

import { getExportTaskParams, useExportPolling } from "../hooks/useExportPolling";
import { useDialogInfo, useDialogReset } from "../../../../hooks/useDialogs";
import { useFileDownloader } from "../hooks/useFileDownloader";
import { usePostgresLayer } from "../../../../hooks/layer/usePostgresLayer";
import { useTableSelectedCount } from "../../../../hooks/table/useTableSelectedCount";
import { useTempTableCondition } from "../../../../hooks/table/useTempTableCondition";
import { layerInfosWithDeps } from "../../../../resources/layers";
import { declension } from "../../../../utils/declension";
import { table } from "../../../../resources/tables";

import { ExportFormat } from "../types";

import { getSelectedFeaturesTotalCounts } from "../../../../ducks/selectFeatures/selectors";

const useExtractData = (
  resourceType,
  resourceName,
  fromTable,
  fromFeatureCard,
): [boolean, unknown[], number, boolean] => {
  const totalCounts = useSelector(getSelectedFeaturesTotalCounts);

  const { selectedCount } = useTableSelectedCount(
    resourceType === ResourceTypeLink.Layer && fromTable ? resourceName : undefined,
  );

  if (resourceType === ResourceTypeLink.Layer) {
    const layerInfos = layerInfosWithDeps.use({ input: [resourceName] });
    const { data, loading } = layerInfos;
    const rowCount = fromFeatureCard
      ? totalCounts?.[resourceName]
      : (fromTable ? selectedCount : data?.deps.tables[0]?.rowCount) ?? null;

    return [
      loading,
      // TODO
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      data?.deps.tables[0] ? data?.deps.tables[0].columns : null,
      rowCount,
      data?.layers[0]?.geometryType === "point",
    ];
  }

  if (resourceType === ResourceTypeLink.Table) {
    const layerInfos = table.use({ input: resourceName });
    const { data, loading } = layerInfos;

    return [loading, data?.columns || null, data?.rowCount || null, false];
  }

  return [false, null, null, false];
};

// eslint-disable-next-line max-lines-per-function
export const Export: FC = memo(() => {
  const {
    resources: [resourceName, resourceAlias, resourceType],
    options: { fromTable, fromFeatureCard },
  } = useDialogInfo<string[], { fromTable?: boolean; fromFeatureCard?: boolean }>();

  const { t } = useTranslation("dialogs");
  const cancelExport = useDialogReset();
  const [isWkt, setIsWkt] = useState<boolean>(true);
  const [columnDelimiter, setColumnDelimiter] = useState<string>(";");
  const { postgresLayer } = usePostgresLayer(resourceName, { waitInput: true });
  const resultCondition = useTempTableCondition(postgresLayer);

  useEffect(() => {
    if (!resourceName) {
      cancelExport();
    }
  }, [resourceName]);

  const [selectedFormat, setSelectedFormat] = useState<IButtonItemProps>({ label: "GeoJSON" });

  const targetName = useMemo(() => {
    if (!selectedFormat?.label) return "";

    const ext = EXPORT_FILE_TYPES.find(item => item.name === selectedFormat.label).extension;

    return `${resourceName}${ext}`;
  }, [resourceName, selectedFormat?.label]);

  const exportTaskParams = useMemo(
    () =>
      getExportTaskParams({
        sourceName: resourceName,
        sourceType: resourceType as ResourceTypeLink,
        targetName,
        format: selectedFormat?.label as ExportFormat,
        columnDelimiter,
        isWkt,
        condition: fromTable || fromFeatureCard ? resultCondition : undefined,
      }),
    [
      resourceName,
      resourceType,
      targetName,
      selectedFormat?.label,
      columnDelimiter,
      isWkt,
      fromTable,
      fromFeatureCard,
      resultCondition,
    ],
  );

  const [{ runExport, exportInProgress, exportDone, resetExport }] = useExportPolling(
    resourceName,
    targetName,
    exportTaskParams,
  );

  const { downloadHiddenLink, downloadInProgress } = useFileDownloader({
    targetName,
    selectedFormat,
    isReady: exportDone,
    reset: resetExport,
    onClickDownload: cancelExport,
  });

  const [layerInfosLoading, columns, rowCount, isPoint] = useExtractData(
    resourceType,
    resourceName,
    fromTable,
    fromFeatureCard,
  );

  const isBrokenLayer = !layerInfosLoading && rowCount === null;

  const memoDescription = useMemo(() => {
    const attributesCount = columns?.length || 0;
    const objWord = declension(t("export.object1"), t("export.object2"), t("export.object3"), rowCount);
    const attrWord = declension(
      t("export.attribute1"),
      t("export.attribute2"),
      t("export.attribute3"),
      attributesCount,
    );
    const tblWord = fromTable ? `${t("export.ofTable")} ` : "";
    const srcWord =
      resourceType === ResourceTypeLink.Layer
        ? t("export.ofLayer")
        : resourceType === ResourceTypeLink.Table
        ? t("export.ofDataSource")
        : "";

    return `${rowCount} ${objWord} ${t("export.and")} ${attributesCount} ${attrWord} ${t(
      "export.from",
    )} ${tblWord}${srcWord} [${resourceAlias}].`;
  }, [resourceAlias, resourceType, rowCount, columns?.length, fromTable, t]);

  const isLoading = layerInfosLoading || exportInProgress || downloadInProgress;

  return (
    <Dialog
      isOpen
      modal
      onCloseRequest={cancelExport}
      maxWidth={EXPORT_DIALOG_STYLE.maxWidth}
      minHeight={isBrokenLayer ? null : EXPORT_DIALOG_STYLE.minHeight}
    >
      <Title cancelExport={cancelExport}>
        {isBrokenLayer ? `${t("export.layerInitError")} ${resourceName}` : t("export.dataExport")}
      </Title>
      {!isBrokenLayer && (
        <>
          <DialogContent>
            <Content>
              <ExportDialogContent
                isLoading={isLoading}
                isPointData={isPoint}
                description={memoDescription}
                selectedFormat={selectedFormat}
                setSelectedFormat={setSelectedFormat}
                isWkt={isWkt}
                setIsWkt={setIsWkt}
                columnDelimiter={columnDelimiter}
                setColumnDelimiter={setColumnDelimiter}
                layer={postgresLayer}
              />
            </Content>
            {downloadHiddenLink}
          </DialogContent>
          <Actions submit={runExport} cancel={cancelExport} loading={isLoading} />
        </>
      )}
    </Dialog>
  );
});
